import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronDown } from 'lucide-react';
import CountryFlag from '@client/modules/countryFlag/countryFlag';
import styles from './countrySelector.scss';

const COUNTRY_OPTIONS = [
  { code: 'SE', name: 'Sweden' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NO', name: 'Norway' },
  { code: 'FI', name: 'Finland' },
];

const LISTBOX_ID = 'admin-sidebar-country-options';

function summary(selected) {
  if (selected.length === 0) {
    return 'Select country';
  }
  if (selected.length === 1) {
    const [code] = selected;
    const known = COUNTRY_OPTIONS.find((option) => option.code === code);
    return known ? known.name : code;
  }
  return `${selected.length} countries`;
}

// Multi-select country picker. Reports every close (outside click, Escape,
// Tab, or the trigger again) through onClose so the caller can apply the choice.
function CountrySelector({ selected, onToggle, onClose }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const focusFirstOnOpen = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  function close(restoreFocus) {
    setOpen(false);
    if (restoreFocus && triggerRef.current) {
      triggerRef.current.focus();
    }
    onCloseRef.current();
  }

  useEffect(() => {
    if (open && focusFirstOnOpen.current && optionRefs.current[0]) {
      optionRefs.current[0].focus();
    }
    focusFirstOnOpen.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handleOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
        onCloseRef.current();
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  function moveFocus(step) {
    const current = optionRefs.current.findIndex(
      (option) => option === document.activeElement
    );
    const count = COUNTRY_OPTIONS.length;
    const next =
      current === -1
        ? step > 0
          ? 0
          : count - 1
        : (current + step + count) % count;
    optionRefs.current[next].focus();
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape' && open) {
      // Handled here, so a surrounding drawer does not also close.
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab' && open) {
      // From a list item, leave from the trigger so a surrounding drawer can
      // keep focus inside itself.
      if (event.target !== triggerRef.current && triggerRef.current) {
        triggerRef.current.focus();
      }
      close(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        focusFirstOnOpen.current = true;
        setOpen(true);
      } else {
        moveFocus(event.key === 'ArrowDown' ? 1 : -1);
      }
    } else if (
      open &&
      event.target !== triggerRef.current &&
      (event.key === ' ' || event.key === 'Enter')
    ) {
      event.preventDefault();
      onToggle(event.target.getAttribute('data-code'));
    }
  }

  return (
    <div
      ref={containerRef}
      className={styles.selector}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? LISTBOX_ID : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className={styles.label}>Country</span>{' '}
        <span className={styles.value}>
          {selected.map((code) => (
            <img
              key={code}
              className={styles.flag}
              src={CountryFlag(code)}
              alt=""
            />
          ))}
          <span className={styles.valueText}>{summary(selected)}</span>
          <ChevronDown className={styles.chevron} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <ul
          id={LISTBOX_ID}
          className={styles.options}
          role="listbox"
          aria-label="Country"
          aria-multiselectable="true"
        >
          {COUNTRY_OPTIONS.map(({ code, name }, index) => {
            const isSelected = selected.includes(code);
            return (
              <li
                key={code}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                role="option"
                aria-selected={isSelected}
                data-code={code}
                tabIndex={-1}
                className={styles.option}
                onClick={() => onToggle(code)}
              >
                <img className={styles.flag} src={CountryFlag(code)} alt="" />
                <span className={styles.optionName}>{name}</span>
                {isSelected && (
                  <Check className={styles.check} aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

CountrySelector.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CountrySelector;
